import { useParams, Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import { articles, comments, categories } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="font-heading font-black text-4xl text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = article.relatedIds
    ?.map((rid) => articles.find((a) => a.id === rid))
    .filter(Boolean) || [];

  const articleComments = comments.filter((c) => c.articleId === article.id);
  const categorySlug = categories.find((c) => c.name === article.categoryName)?.slug || "";

  const breadcrumbItems = [
    { name: article.categoryName || "分类", href: categorySlug ? `/category/${categorySlug}` : undefined },
    { name: article.title },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "53吃瓜",
      logo: {
        "@type": "ImageObject",
        url: `${typeof window !== "undefined" ? window.location.origin : ""}/vite.svg`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": typeof window !== "undefined" ? window.location.href : "",
    },
    articleSection: article.categoryName,
    wordCount: article.excerpt.length + 200,
  };

  const faqSchema = articleComments.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: articleComments.slice(0, 3).map((comment) => ({
          "@type": "Question",
          name: `${comment.author}的评论`,
          acceptedAnswer: {
            "@type": "Answer",
            text: comment.content,
          },
        })),
      }
    : null;

  const jsonLd = faqSchema ? [articleSchema, faqSchema] : articleSchema;

  return (
    <>
      <SEOHead
        title={`${article.title} - 53吃瓜`}
        description={article.excerpt}
        ogImage={article.image}
        ogType="article"
        articlePublishDate={`${article.publishDate}T00:00:00+08:00`}
        articleModifiedDate={`${article.publishDate}T00:00:00+08:00`}
        author={article.author}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 pb-12 md:pb-16 bg-foreground-950 min-h-[350px] md:min-h-[450px] flex items-end">
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500/20 text-primary-400 text-xs font-bold mb-4 mt-4">
              {article.categoryName}
            </span>
            <h1 className="font-heading font-black text-white text-3xl md:text-4xl lg:text-5xl leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-white/60 text-sm flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount} 阅读
              </span>
            </div>
          </div>
        </div>
      </section>

      <main className="bg-background-50">
        {/* Article Content */}
        <section className="py-10 md:py-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              {/* Main Image */}
              <div className="rounded-2xl md:rounded-3xl overflow-hidden mb-8 shadow-lg">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full aspect-video object-cover object-top"
                />
              </div>

              {/* Content */}
              <article className="prose prose-lg max-w-none">
                <div
                  className="text-foreground-700 text-base md:text-lg leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </article>

              {/* Share */}
              <div className="flex items-center gap-3 mt-10 pt-8 border-t border-background-200">
                <span className="text-foreground-500 text-sm font-medium">分享：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-primary-500 hover:text-white transition-colors" aria-label="微博分享">
                  <i className="ri-weibo-fill" />
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-green-500 hover:text-white transition-colors" aria-label="微信分享">
                  <i className="ri-wechat-fill" />
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-foreground-900 hover:text-white transition-colors" aria-label="复制链接">
                  <i className="ri-link" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Comments */}
        {articleComments.length > 0 && (
          <section className="py-10 md:py-16 bg-background-100">
            <div className="w-full px-4 md:px-8 lg:px-12">
              <div className="max-w-4xl mx-auto">
                <h2 className="font-heading font-black text-foreground-950 text-2xl md:text-3xl mb-8">
                  精选神评
                </h2>
                <div className="space-y-4">
                  {articleComments.map((comment) => (
                    <div key={comment.id} className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/70">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-3-line text-secondary-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="font-bold text-foreground-950 text-sm">{comment.author}</span>
                            <i className="ri-verified-badge-fill text-green-500 text-sm" />
                            <span className="text-foreground-400 text-xs">{comment.date}</span>
                          </div>
                          <p className="text-foreground-700 text-sm leading-relaxed">{comment.content}</p>
                          <div className="flex items-center gap-4 mt-3">
                            <button className="flex items-center gap-1 text-foreground-400 text-xs hover:text-primary-500 transition-colors">
                              <i className="ri-thumb-up-line" />
                              {comment.likes}
                            </button>
                            <button className="flex items-center gap-1 text-foreground-400 text-xs hover:text-primary-500 transition-colors">
                              <i className="ri-chat-1-line" />
                              回复
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-16">
            <div className="w-full px-4 md:px-8 lg:px-12">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading font-black text-foreground-950 text-2xl md:text-3xl mb-8">
                  猜你喜欢
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <article key={ra!.id} className="group">
                      <Link to={`/article/${ra!.id}`} className="block">
                        <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all hover:shadow-lg">
                          <div className="aspect-[16/10] overflow-hidden">
                            <img
                              src={ra!.image}
                              alt={ra!.title}
                              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                          <div className="p-5">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium">
                                {ra!.categoryName}
                              </span>
                              <span className="text-foreground-400 text-xs">{ra!.publishDate}</span>
                            </div>
                            <h3 className="font-heading font-bold text-foreground-950 text-base leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                              {ra!.title}
                            </h3>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}